import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { animated, useTransition } from '@react-spring/web';
import { differenceWith, isEqual } from 'es-toolkit';
import { cloneElement, memo, useCallback, useMemo, useRef, useState } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { FormattedCurrency } from '@/components/Formatted/FormattedCurrency';
import { FormattedPreferredCurrency } from '@/components/Formatted/FormattedPreferredCurrency';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';
import useWindowSize from '@/hooks/useWindowSize';
import { useAppSelector } from '@/redux/hooks';
import { getMessages } from '@/redux/modules/consoleMessages';

import Messages, { MessageProps } from './helpers/Messages';

type MessageData = {
    amount: number;
    bidderId: number;
    currency: string;
    limit: number;
    lotNumber: string;
    myBid: boolean;
    paddleNumber: string;
    previousBidAmount: number;
    serverCreatedTime: string;
    source: string;
};

type Message = {
    created: any;
    data: MessageData;
    messageId: any;
    text: string;
    type: string;
};

type Props = {
    catalogId: number;
    className?: string;
    convert?: boolean;
    hideMessages?: boolean;
    maxMessages?: number;
    overlay?: boolean;
    smallConsole?: boolean;
    zIndex?: number;
};

const NARROW_SCROLLER_HEIGHT = 124;
const WIDE_SCROLLER_HEIGHT = 150;

const MessageScroller = ({
    catalogId,
    className,
    convert = false,
    hideMessages,
    maxMessages,
    overlay,
    smallConsole,
    zIndex,
}: Props) => {
    const messageList = useAppSelector((state) => getMessages(state, catalogId, smallConsole), isEqual) || [];
    const { height: windowHeight } = useWindowSize();
    const refMap = useMemo(() => new WeakMap(), []);
    const cancelMap = useMemo(() => new WeakMap(), []);
    const [items, setItems] = useState<Array<Message & { _uid: string }>>([]);
    const narrow = useIsNarrowerThan(1000);
    const uidCounter = useRef(0);

    // Generate truly unique key using assigned uid
    const getMessageKey = useCallback((item: Message & { _uid?: string }) => {
        return item._uid || `${item.created}-${item.type}`;
    }, []);

    const buildMessage = useCallback(
        ({ created, data = {} as MessageData, messageId, text, type }: Message) => {
            const {
                amount = 0,
                bidderId,
                currency = 'usd',
                limit = 0,
                lotNumber,
                myBid,
                paddleNumber,
                serverCreatedTime,
            } = data;

            const nonLaBidder = bidderId === 0;

            // Converted currency sits on the same light background as the log text,
            // so it should use primary text color instead of inverse.
            const usePrimaryConvertedCurrencyText =
                ((type === 'accepted' || type === 'accepted2') && !nonLaBidder) ||
                type === 'opened' ||
                type === 'outbid' ||
                type === 'outbid-absentee' ||
                type === 'absentee-outbid' ||
                type === 'mybid' ||
                type === 'askChanged' ||
                type === 'sold';

            const intlValues = {
                amount: (
                    <FormattedCurrency
                        className="pl-[3px] !text-text-primary"
                        key="message-scroller-amount"
                        noFraction
                        sourceCurrency={currency || 'usd'}
                        value={amount || 0}
                    />
                ),
                converted: convert ? (
                    <div
                        className="inline-flex pr-[3px]"
                        key="message-scroller-converted"
                    >
                        <FormattedPreferredCurrency
                            colorPrimary={usePrimaryConvertedCurrencyText}
                            currencyClassName="p-0 pt-px"
                            noBackground
                            noFraction
                            sourceCurrency={currency || 'usd'}
                            sourceValue={amount || 0}
                            wrapWithParens
                        />
                    </div>
                ) : null,
                limit: (
                    <FormattedCurrency
                        key="message-scroller-limit"
                        noFraction
                        sourceCurrency={currency || 'usd'}
                        value={limit || 0}
                    />
                ),
                lotNumber,
                paddleNumber,
                serverCreatedTime,
            };

            const messageProps: MessageProps = {
                bidderId,
                created,
                hideMessage: false,
                intlValues,
                limit,
                messageId,
                myBid,
                nonLaBidder,
                overlay,
                text,
            };

            // In case we get an unhandled type
            const MessageBuilder = Messages[type] || (() => null);

            return (
                <div>
                    <MessageBuilder {...messageProps} />
                </div>
            );
        },
        [convert, overlay]
    );

    const displayedItems = maxMessages ? items.slice(-maxMessages) : items;

    const transitions = useTransition(displayedItems, {
        enter: (item) => async (next, cancel) => {
            cancelMap.set(item, cancel);
            await next({ height: refMap.get(item).offsetHeight, opacity: 1 });
        },
        from: { height: 0, opacity: 0 },
        keys: getMessageKey,
        // we only keep 80 messages max. this is as many as would show with a vertical 4k monitor
        onRest: () => setItems((state) => state.slice(-80)),
    });

    useDeepCompareEffect(() => {
        setItems((currentItems) => {
            // Compute new items inside callback to use actual current state (fixes React StrictMode double-render)
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const currentItemsWithoutUid = currentItems.map(({ _uid: _, ...rest }) => rest);
            const actualNewItems = differenceWith(messageList, currentItemsWithoutUid, isEqual);
            if (actualNewItems.length === 0) {
                return currentItems;
            }
            // Assign unique IDs to new items to guarantee unique keys
            const itemsWithUid = actualNewItems.map((item) => ({
                ...item,
                _uid: `msg-${uidCounter.current++}`,
            }));
            return [...currentItems, ...itemsWithUid];
        });
    }, [messageList]);

    const height = narrow ? NARROW_SCROLLER_HEIGHT : WIDE_SCROLLER_HEIGHT;
    const scrollerHeight = hideMessages ? height : windowHeight;

    return (
        <div
            className={cn(
                'pointer-events-none absolute bottom-0 left-0 right-0 max-w-full touch-none select-none overflow-hidden',
                className
            )}
            data-testid="console-message-scroller"
            style={{ maxHeight: scrollerHeight, minHeight: scrollerHeight, zIndex: zIndex || 'auto' }}
        >
            <ul
                className={cn(
                    'absolute inset-x-0 bottom-0 m-0 flex list-none flex-col overflow-hidden p-0',
                    overlay ? 'gap-2xs pb-2xs' : 'gap-xs pb-xs'
                )}
            >
                {transitions(({ ...style }, item) => (
                    <animated.li
                        className="relative block w-full overflow-hidden"
                        data-testid="message"
                        style={style}
                    >
                        {cloneElement(buildMessage(item), {
                            ref: (ref: HTMLDivElement) => ref && refMap.set(item, ref),
                        })}
                    </animated.li>
                ))}
            </ul>
        </div>
    );
};

export default memo<Props>(MessageScroller);
